/**
 * MoneyInput — free-entry numeric input for dollar amounts.
 *
 * Phase 30.4: replaces `<Input type="number">` for currency fields. The
 * default number-input spinner is poor UX for currency (no one clicks an
 * up-arrow 50,000 times). This component accepts free-entry numeric input
 * with comma separators, displays formatted with commas when not focused,
 * and stores a plain number through the standard `value` / `onChange`
 * contract.
 *
 * Behavior:
 *  - Accepts digits, optional one leading `-`, an optional decimal point,
 *    and commas (which are ignored on parse). All other characters are
 *    stripped on input.
 *  - Displays the value with thousands separators when the field is blurred
 *    (e.g. 50000 -> "50,000"). While focused, shows the raw digits the user
 *    is typing so the cursor doesn't fight comma-insertion.
 *  - Empty string parses to 0 in the parent; the parent never sees NaN.
 *  - Works as a drop-in for `<Input type="number">` for our use cases.
 *
 * Negative amounts are allowed (change requests can have negative cost
 * impact representing a saving). If you ever need to restrict to
 * non-negative, pass `min={0}` and a leading `-` will be stripped.
 */

import { useState, useEffect, type ChangeEvent, type FocusEvent } from 'react';
import { Input } from '@/components/ui/Input';

interface MoneyInputProps {
  value: number;
  onChange: (next: number) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Pass 0 to forbid negative numbers; omit to allow them. */
  min?: number;
  className?: string;
  'aria-label'?: string;
}

/**
 * Sanitize a typed string for display.
 *
 * Phase 30.8.1: commas are preserved here — users naturally type commas in
 * large numbers (e.g. "50,000") and removing them mid-keystroke feels
 * broken. The numeric value is computed separately by parseAmount(), which
 * does strip commas before parseFloat (since parseFloat("50,000") stops at
 * the comma and returns 50, which would be wrong).
 *
 * What this function strips:
 *   - any character that isn't a digit, comma, dot, or leading minus
 *   - multiple decimal points (keeps the first)
 *   - minus signs anywhere except position 0
 *   - all minus signs when allowNegative is false
 */
function sanitize(raw: string, allowNegative: boolean): string {
  let s = raw;
  if (!allowNegative) s = s.replace(/-/g, '');
  // Only the first character may be a minus sign; strip any others.
  s = s.replace(/(?!^)-/g, '');
  // Only one decimal point.
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  // Strip anything that isn't a digit, comma, dot, or minus.
  s = s.replace(/[^0-9.,-]/g, '');
  return s;
}

/**
 * Parse a sanitized string to a number, treating empty/lone-separators as 0.
 *
 * Commas are stripped here (not in sanitize) so the visible draft keeps the
 * user's typed commas while the numeric value remains correct.
 */
function parseAmount(s: string): number {
  const stripped = s.replace(/,/g, '');
  if (stripped === '' || stripped === '-' || stripped === '.' || stripped === '-.') return 0;
  const n = parseFloat(stripped);
  return Number.isFinite(n) ? n : 0;
}

/** Format a number with thousands separators for display. */
function formatAmount(n: number): string {
  // Avoid scientific notation for huge values; restrict to a sensible
  // fractional precision. We accept up to 2 decimals on input but display
  // whatever the underlying number is.
  if (!Number.isFinite(n)) return '';
  // toLocaleString handles thousands separators; no trailing zeros.
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function MoneyInput({
  value,
  onChange,
  id,
  placeholder,
  disabled,
  min,
  className,
  'aria-label': ariaLabel,
}: MoneyInputProps) {
  const allowNegative = min === undefined || min < 0;

  // While focused we show the raw digits so the cursor doesn't jump as
  // commas are inserted. When blurred we show the comma-formatted version.
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string>(() =>
    value === 0 ? '' : String(value),
  );

  // When the external value changes (and we're not focused), reset the draft
  // so the formatted display follows the value.
  useEffect(() => {
    if (!focused) {
      setDraft(value === 0 ? '' : String(value));
    }
  }, [value, focused]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const clean = sanitize(e.target.value, allowNegative);
    setDraft(clean);
    onChange(parseAmount(clean));
  };

  const handleFocus = () => {
    setFocused(true);
    // Show the raw digits, not the formatted version, while editing.
    setDraft(value === 0 ? '' : String(value));
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    // On blur, snap the draft to a canonical formatted representation.
    setDraft(value === 0 ? '' : formatAmount(value));
  };

  return (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      value={focused ? draft : value === 0 ? '' : formatAmount(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder ?? '0'}
      disabled={disabled}
      className={className}
      aria-label={ariaLabel}
    />
  );
}
